import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Lock } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatError } from "@/lib/api";

export default function LoginPage() {
  const { user, login, loading } = useAuth();
  const [email, setEmail] = useState("admin@tvs.com");
  const [password, setPassword] = useState("admin123");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  if (!loading && user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/dashboard");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="relative hidden lg:block">
        <img
          src="https://images.unsplash.com/photo-1656767323058-49cb61bfda4b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxibHVlJTIwbW90b3JjeWNsZXxlbnwwfHx8fDE3ODExNTExMDN8MA&ixlib=rb-4.1.0&q=85"
          alt="TVS"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#253c80]/95 via-[#253c80]/85 to-[#1a2a5e]/95" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-lg bg-white/15 grid place-items-center ring-1 ring-white/30">
              <ShieldCheck weight="duotone" size={28} />
            </div>
            <div>
              <div className="font-extrabold text-2xl tracking-tight">TVS Genuine Parts</div>
              <div className="text-xs uppercase tracking-[0.22em] text-white/75">
                Anti-Counterfeit QR System
              </div>
            </div>
          </div>

          <div className="space-y-5">
            <h1 className="text-4xl xl:text-5xl font-extrabold leading-tight tracking-tight">
              Every part. Verified.
              <br />
              <span className="text-white/80">Every customer. Protected.</span>
            </h1>
            <p className="text-white/80 max-w-md text-base">
              Generate tamper-proof QR codes, track scans in real-time, and stop counterfeiting at the source.
            </p>
          </div>

          <div className="text-xs text-white/60">© {new Date().getFullYear()} TVS Motor Company · Internal Tool</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 bg-slate-50">
        <div className="w-full max-w-sm">
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#253c80]/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em] text-[#253c80]">
              <Lock size={12} weight="bold" /> Admin Access
            </div>
            <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-slate-900">Sign in</h2>
            <p className="mt-1 text-sm text-slate-600">
              Use your admin credentials to access the QR control center.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@tvs.com"
                required
                className="mt-1.5 h-11 text-base"
                data-testid="login-email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="mt-1.5 h-11 text-base"
                data-testid="login-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-11 bg-[#253c80] hover:bg-[#1a2a5e] text-white font-semibold"
              data-testid="login-submit-button"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </Button>

            <div className="text-xs text-slate-500 text-center bg-slate-100 rounded-md p-3 border border-slate-200">
              Default credentials: <span className="font-mono font-semibold">admin@tvs.com</span> /{" "}
              <span className="font-mono font-semibold">admin123</span>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
